import {
    object,
    string,
    boolean,
    number,
    array,
    mixed,
    date,
    ObjectSchema,
    InferType,
} from "yup";

import { CostTypeEnum } from "../../empresa/contracts/labor-budget";
import { UnitOfMeasureEnum } from "../../../shared/components/formulario/select/SelectUnitOfMeasure";
import { DiscountTypeEnum, PersonTypeEnum } from "../../../shared/utils/enum/enum";
import { EditarOrcamentoContract, StutusBudgetEnum } from "../contracts/editar-orcamento.contract";

export const editarOrcamentoSchema: ObjectSchema<EditarOrcamentoContract> = object({
    description: string().required("Descrição é obrigatória!"),

    status: mixed<StutusBudgetEnum>()
        .oneOf(Object.values(StutusBudgetEnum))
        .required("Status do orçamento é obrigatório!"),

    isClient: boolean().required("Informe se é cliente cadastrado!"),

    idClient: string().nullable().when("isClient", {
        is: true,
        then: (schema) => schema.required("Cliente é obrigatório!"),
        otherwise: (schema) => schema.optional(),
    }),

    isProfitMargin: boolean().required("Informe se possui margem de lucro!"),

    idProfitMargin: string().when("isProfitMargin", {
        is: true,
        then: (schema) => schema.required("Margem de lucro é obrigatória!"),
        otherwise: (schema) => schema.optional(),
    }),

    percentageExtraCost: number()
        .transform((value, originalValue) => {
            if (typeof originalValue === "string") {
                // Remove tudo que não for dígito ou vírgula
                let cleaned = originalValue.replace(/[^\d,]/g, "");
                // Troca a última vírgula por ponto (caso exista)
                const lastComma = cleaned.lastIndexOf(",");
                if (lastComma !== -1) {
                    cleaned =
                        cleaned.slice(0, lastComma).replace(/,/g, "") +
                        "." +
                        cleaned.slice(lastComma + 1);
                }
                if (!cleaned) return undefined;
                const valorNumber = Number(cleaned);
                return isNaN(valorNumber) ? undefined : valorNumber;
            }
            return value;
        })
        .optional(),

    isDiscount: boolean().required("Informe se deseja aplicar desconto!"),
    discountType: mixed<DiscountTypeEnum>()
        .oneOf(Object.values(DiscountTypeEnum))
        .when("isDiscount", {
            is: true,
            then: (schema) => schema.required("Tipo de desconto é obrigatório!"),
            otherwise: (schema) => schema.optional(),
        }),
    discountValue: number()
        .typeError("Valor total deve ser um número")
        .transform((value, originalValue) => {
            if (typeof originalValue === "string") {
                const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
                if (!cleaned) return undefined;
                const valorNumber = Number(cleaned);
                return isNaN(valorNumber) ? undefined : valorNumber;
            }
            return value;
        })
        .required("É obrigatório informar o valor da movimentação!")
        .default(0),

    fantasyName: string().optional(),

    personType: mixed<PersonTypeEnum>()
        .oneOf(Object.values(PersonTypeEnum))
        .nullable()
        .defined()
        .when("isClient", {
            is: false,
            then: (schema) => schema.required("Tipo de pessoa é obrigatório!"),
        })
        .default(null),

    cnpj: string().when(
        ["isClient", "personType"],
        ([isClient, personType], schema) => {
            if (!isClient && personType === PersonTypeEnum.PJ) {
                return schema.required("CNPJ é obrigatório!");
            }
            return schema.optional();
        }
    ),

    corporateName: string().when(
        ["isClient", "personType"],
        ([isClient, personType], schema) => {
            if (!isClient && personType === PersonTypeEnum.PJ) {
                return schema.required("Razão social é obrigatória!");
            }
            return schema.optional();
        }
    ),

    cpf: string().when(
        ["isClient", "personType"],
        ([isClient, personType], schema) => {
            if (!isClient && personType === PersonTypeEnum.PF) {
                return schema.required("CPF é obrigatório!");
            }
            return schema.optional();
        }
    ),

    fullName: string().when(
        ["isClient", "personType"],
        ([isClient, personType], schema) => {
            if (!isClient && personType === PersonTypeEnum.PF) {
                return schema.required("Nome é obrigatório!");
            }
            return schema.optional();
        }
    ),

    email: string().email("E-mail inválido!").optional(),

    phone: string().when("isClient", {
        is: false,
        then: (schema) => schema.required("Telefone é obrigatório!"),
        otherwise: (schema) => schema.optional(),
    }),

    dateValidation: date().required("Data de validade é obrigatória!"),

    observation: string().nullable().optional(),

    address: object({
        zipCode: string().required("CEP é obrigatório!"),
        street: string().required("Rua é obrigatória!"),
        number: string().required("Número é obrigatório!"),
        neighborhood: string().required("Bairro é obrigatório!"),
        city: string().required("Cidade é obrigatória!"),
        state: string().required("Estado é obrigatório!"),
        complement: string().optional(),
    }).required("Endereço é obrigatório!"),

    steps: array(
        object({
            id: string().optional(),

            description: string().required("Descrição do passo é obrigatória!"),

            costType: mixed<CostTypeEnum>()
                .oneOf(Object.values(CostTypeEnum))
                .required("Tipo de custo é obrigatório!"),

            mediumTerm: number().optional(),

            itens: array(
                object({
                    id: string().optional(),

                    isLabor: boolean().required("Informe se é mão de obra!"),

                    idLaborBudget: string().nullable().when("isLabor", {
                        is: true,
                        then: (schema) =>
                            schema.required(
                                "ID do orçamento de mão de obra é obrigatório!"
                            ),
                        otherwise: (schema) => schema.optional(),
                    }),

                    description: string().when("isLabor", {
                        is: false,
                        then: (schema) =>
                            schema.required("Descrição do item é obrigatória!"),
                        otherwise: (schema) => schema.optional(),
                    }),

                    quantity: number()
                        .typeError("Quantidade inválida!")
                        .min(1, "Quantidade deve ser maior que zero!")
                        .required("Quantidade é obrigatória!"),

                    unitOfMeasure: mixed<UnitOfMeasureEnum>().when("isLabor", {
                        is: false,
                        then: (schema) =>
                            schema
                                .oneOf(Object.values(UnitOfMeasureEnum))
                                .required("Unidade de medida é obrigatória!"),
                        otherwise: (schema) => schema.optional(),
                    }),

                    unitValue: number()
                        .transform((value, originalValue) => {
                            if (typeof originalValue === "string") {
                                // Remove tudo que não for dígito ou vírgula
                                let cleaned = originalValue.replace(/[^\d,]/g, "");
                                // Troca a última vírgula por ponto (caso exista)
                                const lastComma = cleaned.lastIndexOf(",");
                                if (lastComma !== -1) {
                                    cleaned =
                                        cleaned.slice(0, lastComma).replace(/,/g, "") +
                                        "." +
                                        cleaned.slice(lastComma + 1);
                                }
                                if (!cleaned) return undefined;
                                const valorNumber = Number(cleaned);
                                return isNaN(valorNumber) ? undefined : valorNumber;
                            }
                            return value;
                        })
                        .when("isLabor", {
                            is: false,
                            then: (schema) =>
                                schema
                                    .typeError("Valor inválido!")
                                    .min(0, "Valor deve ser maior ou igual a zero!")
                                    .required("Valor unitário é obrigatório!"),
                            otherwise: (schema) => schema.optional(),
                        }),
                })
            )
                .min(1, "Informe pelo menos um item!")
                .required("Itens do passo são obrigatórios!"),
        })
    )
        .min(1, "Informe pelo menos um passo!")
        .required("Passos do orçamento são obrigatórios!"),
});

export type EditarOrcamentoFormData = InferType<typeof editarOrcamentoSchema>;